<template>
  <div class="mt-2 mb-4 readiness-page">
    <div class="readiness-title-row">
      <div class="readiness-header">
        Readiness
      </div>
      <HelpMenu />
    </div>
    <ReadinessMenu 
      :menuItems="menuItems" 
      :initialTab="initialTab" 
      @tab-selected="selectTab"
    />
    <div class="readiness-content" v-if="selectedTab === 'Employees'">
      <EmployeesReadinessTab
        :averageReadiness="averageReadiness"
        :last_month_averageReadiness="last_month_averageReadiness"
        :employees="employees"
        :filterGroups="filterGroups"
        :appliedFilters="appliedFilters"
        :searchText="searchText"
        :totalCount="totalCount"
        :currentPage="currentPage"
        :itemsPerPage="itemsPerPage"
        :toggleLoader="toggleLoader"
        :loading="employeesLoading"
        :recalculating="recalculating"
        @onChatterClick="$emit('onChatterClick', $event)"
        @onSearch="$emit('onSearch', $event)"
        @onFilterChange="$emit('onFilterChange', $event)"
        @onRemoveFilter="$emit('onRemoveFilter', $event)"
        @onClearFilters="$emit('onClearFilters', $event)"
        @onExcludeChange="$emit('onExcludeChange', $event)"
        @onPaginationChange="$emit('onPaginationChange', $event)"
        @onEmployeeClick="$emit('onEmployeeClick', $event)"
        @onImportanceChange="$emit('onImportanceChange', $event)"
      />
    </div>
    <div class="readiness-content" v-if="selectedTab === 'Positions'">
      <PositionsReadinessTab
        :averageReadiness="averageReadiness"
        :positionsAverageReadiness="positionsAverageReadiness"
        :last_month_averageReadiness="last_month_averageReadiness"
        :positions="positions"
        :filterGroups="positionsFilterGroups"
        :appliedFilters="positionsAppliedFilters"
        :searchText="positionsSearchText"
        :totalCount="positionsTotalCount"
        :currentPage="positionsCurrentPage"
        :itemsPerPage="positionsItemsPerPage"
        :toggleLoader="positionsToggleLoader"
        :departments="departments"
        :departmentLoading="departmentLoading"
        :initialDepartment="initialDepartment"
        :initialScope="initialScope"
        @onChatterClick="$emit('onPositionsChatterClick', $event)"
        @onSearch="$emit('onPositionsSearch', $event)"
        @onFilterChange="$emit('onPositionsFilterChange', $event)"
        @onRemoveFilter="$emit('onPositionsRemoveFilter', $event)"
        @onClearFilters="$emit('onPositionsClearFilters', $event)"
        @onExcludeChange="$emit('onPositionsExcludeChange', $event)"
        @onPaginationChange="$emit('onPositionsPaginationChange', $event)"
        @onPositionClick="$emit('onPositionClick', $event)"
        @onDepartmentSearch="$emit('onDepartmentSearch', $event)"
        @onDepartmentClear="$emit('onDepartmentClear', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import ReadinessMenu from "@components/ReadinessMenu/ReadinessMenu.vue";
import HelpMenu from "@components/HelpMenu/HelpMenu.vue";
import EmployeesReadinessTab from "@components/EmployeesReadinessTab/EmployeesReadinessTab.vue";
import PositionsReadinessTab from "@components/PositionsReadinessTab/PositionsReadinessTab.vue";
const emit = defineEmits(["tab-selected", "onChatterClick", "onSearch", "onFilterChange", "onRemoveFilter", "onClearFilters", "onExcludeChange", "onPaginationChange", "onEmployeeClick", "onImportanceChange", "onPositionsChatterClick", "onPositionsSearch", "onPositionsFilterChange", "onPositionsRemoveFilter", "onPositionsClearFilters", "onPositionsExcludeChange", "onPositionsPaginationChange", "onPositionClick", "onDepartmentSearch", "onDepartmentClear"]);

const props = defineProps({
  menuItems: {
    type: Array,
    required: true,
  },
  initialTab: {
    type: String,
    default: "Employees",
  },
  averageReadiness: {
    type: Number,
    default: 0,
  },
  last_month_averageReadiness: {
    type: [Number, null],
    default: null,
  },
  positionsAverageReadiness: {
    type: Number,
    default: null,
  },
  employees: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: "",
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  toggleLoader: {
    type: Boolean,
    default: false,
  },
  employeesLoading: {
    type: Boolean,
    default: false,
  },
  recalculating: {
    type: Boolean,
    default: false,
  },
  positions: {
    type: Array,
    default: () => [],
  },
  positionsFilterGroups: {
    type: Array,
    default: () => [],
  },
  positionsAppliedFilters: {
    type: Array,
    default: () => [],
  },
  positionsSearchText: {
    type: String,
    default: "",
  },
  positionsTotalCount: {
    type: Number,
    default: 0,
  },
  positionsCurrentPage: {
    type: Number,
    default: 1,
  },
  positionsItemsPerPage: {
    type: Number,
    default: 10,
  },
  positionsToggleLoader: {
    type: Boolean,
    default: false,
  },
  departments: {
    type: Array,
    default: () => [],
  },
  departmentLoading: {
    type: Boolean,
    default: false,
  },
  initialDepartment: {
    type: Object,
    default: null,
  },
  initialScope: {
    type: String,
    default: "subtree",
  },
});

const selectedTab = ref(props.initialTab);

const selectTab = (payload) => {
  const label = typeof payload === "string" ? payload : payload?.label;
  if (!label) return;
  selectedTab.value = label;
  emit("tab-selected", label);
};

watch(
  () => props.initialTab,
  (newVal) => {
    selectedTab.value = newVal;
  },
  { immediate: true }
);
</script>

<style scoped>
.readiness-title-row {
  display: flex;
  align-items: center;
  gap: 7px;
}
.readiness-header {
  flex: 1;
  min-width: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #101828;
}
</style>
